import { memo, useRef } from 'react';
import type { Cell, Row } from '@tanstack/react-table';
import { flexRender } from '@tanstack/react-table';
import { styled, TableCell as MuiTableCell, Typography, Tooltip, Box } from '@mui/material';
import { KeyboardArrowRight, KeyboardArrowDown } from '@mui/icons-material';

import type { DataSchema, Schema } from 'types';
import { getUIDefinitionIconStyles } from '../../../utils/styles';
import { useIsOverflowing } from '../../../utils/hooks';
import { getHeliosCell } from 'utils/table/user/helios/heliosCell';
import { getCommonPinningStyles } from './tableFunctions';
import { mapTextAlignToCss } from '../../../utils/styles.ts';
import { TextArea } from 'components/mui/common/TextArea';
import type { TableColumnAggregationState } from 'utils/table';
import { TableCellHeliosResizableTextArea } from './TableCellHeliosResizableTextArea.tsx';
import { sanitizeRichText } from 'utils/quill';
import { HeliosRichTextCell } from './HeliosRichText.styles';

const StyledTableCell = styled(MuiTableCell)(() => ({
  paddingTop: 0,
  paddingBottom: 0,
  minHeight: '52px',
}));

interface TableCellProps<T extends keyof P, P extends DataSchema> {
  cell: Cell<Schema<T, P>, unknown>;
  dataSchema: P;
  entireRowClicked?: (row: Schema<T, P>) => void;
  row: Row<Schema<T, P>>;
  isInnerTableOpen?: boolean;
  clickableCellsKeys: (keyof P)[] | 'all';
  disabled?: boolean;
  isNew?: boolean;
  /**
   * This prop is needed to trigger a re-render when column aggregation changes.
   * It is not used in the function body, but its presence in the props object ensures that
   * React.memo detects a change and refreshes grouped/aggregated cell output.
   */
  columnAggregation?: TableColumnAggregationState;
}

const TableCellBase = <T extends keyof P, P extends DataSchema>({ cell, dataSchema, entireRowClicked, row, isNew = false }: TableCellProps<T, P>) => {
  const typographyRef = useRef<HTMLInputElement | null>(null);

  const { displayValue, valueForRender, uiDefinition, Icon, boxStyles, textStyles, isTextArea, isRichText, valueTextAlign } = getHeliosCell({
    cell,
    row,
    dataSchema,
  });
  const { isOverflowing } = useIsOverflowing(typographyRef, displayValue, 3);

  const cellTextAlign = mapTextAlignToCss(valueTextAlign);

  const isGrouped = cell.getIsGrouped();
  const isAggregated = cell.getIsAggregated();
  const isPlaceholder = cell.getIsPlaceholder();
  const isRowExpanded = row.getIsExpanded();

  const handleRowExpand = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.stopPropagation();
    row.getToggleExpandedHandler()();
  };

  const buildRenderCell = (renderer = cell.column.columnDef.cell) => {
    const renderValue = isAggregated ? cell.getValue() : valueForRender;

    return flexRender(renderer, {
      ...cell.getContext(),
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      getValue: () => renderValue as any,
    });
  };

  const GroupedContent = () => (
    <Box
      sx={{ display: 'flex', alignItems: 'center', maxWidth: '100%', overflow: 'hidden', cursor: row.getCanExpand() ? 'pointer' : 'normal' }}
      onClick={handleRowExpand}>
      {isRowExpanded ? (
        <KeyboardArrowDown fontSize="small" sx={{ mr: 0.5, flexShrink: 0 }} />
      ) : (
        <KeyboardArrowRight fontSize="small" sx={{ mr: 0.5, flexShrink: 0 }} />
      )}
      <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{buildRenderCell()}</Box>
      <Box component="span" sx={{ ml: 0.75, flexShrink: 0 }}>
        ({row.subRows.length})
      </Box>
    </Box>
  );

  const renderCellContent = (textAreaMode = false, richTextMode = false) => {
    switch (true) {
      case isGrouped:
        return <GroupedContent />;
      case isAggregated:
        return buildRenderCell(cell.column.columnDef.aggregatedCell ?? cell.column.columnDef.cell);
      case isPlaceholder:
        return null;
      default:
        if (richTextMode) {
          return (
            <HeliosRichTextCell
              sx={{
                ...boxStyles,
                ...textStyles,
              }}>
              <div
                className="ql-editor"
                style={{ textAlign: cellTextAlign }}
                dangerouslySetInnerHTML={{ __html: sanitizeRichText(displayValue ? displayValue.toString() : '') }}
              />
            </HeliosRichTextCell>
          );
        }
        return textAreaMode ? (
          <TextArea
            value={displayValue ? displayValue.toString() : ''}
            readOnly
            fullWidth
            maxRows={3}
            minHeight={null}
            style={{ textAlign: cellTextAlign }}
            formItemUiDefinition={uiDefinition}
          />
        ) : (
          buildRenderCell()
        );
    }
  };

  if (isRichText) {
    return (
      <TableCellHeliosResizableTextArea
        cell={cell}
        entireRowClicked={entireRowClicked}
        row={row}
        renderCellContent={() => renderCellContent(false, true)}
      />
    );
  }

  if (isTextArea) {
    return <TableCellHeliosResizableTextArea cell={cell} entireRowClicked={entireRowClicked} row={row} renderCellContent={renderCellContent} />;
  }

  return (
    <StyledTableCell
      key={cell.id}
      sx={{
        ...getCommonPinningStyles(cell.column, { id: cell.row.id }),
      }}
      onClick={(e) => {
        e.stopPropagation();
        entireRowClicked?.(row.original);
      }}>
      <Tooltip title={isOverflowing && displayValue ? displayValue.toString() : ''} disableHoverListener={!isOverflowing || !displayValue}>
        <Typography
          variant="subtitle2"
          component="div"
          ref={typographyRef}
          sx={{
            width: '100%',
            maxWidth: '100%',
            display: 'flex',
            alignItems: 'center',
            overflow: 'hidden',
            py: uiDefinition ? '8px' : '4px',
            px: '8px',
            lineHeight: '24px',
            ...boxStyles,
          }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              width: '100%',
              minWidth: 0,
              maxWidth: '100%',
              overflow: 'hidden',
              justifyContent: cellTextAlign === 'left' ? 'flex-start' : cellTextAlign === 'right' ? 'flex-end' : 'center',
            }}>
            {Icon ? <Icon sx={{ ...getUIDefinitionIconStyles(uiDefinition), flexShrink: 0, marginRight: '4px' }} /> : null}
            <Box
              sx={{
                flex: '0 1 auto',
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                textAlign: cellTextAlign,
                ...textStyles,
              }}>
              {!isNew && renderCellContent()}
            </Box>
          </Box>
        </Typography>
      </Tooltip>
    </StyledTableCell>
  );
};
export const TableCellHelios = memo(TableCellBase) as typeof TableCellBase;
